import React from "react";
import { FormControl, MenuItem, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import styled from "styled-components";
import { useStudyPlan } from "../../../../app/contexts/study.context";
import CustomDatePicker from "../../../../app/components/custom.date.picker";
import CustomSwitch from "../../../../app/components/switch";
import CurrencyDropdown from "../../../../app/components/custom.dropdown/currency.dropdown";
import {
  VISIT_INTERVAL_TYPE_OPTIONS,
  CDR_REVIEW_OPTIONS,
  ADDITIONAL_TIME_ON_SITE_FACTOR,
  ESTIMATED_ACTIVATED_NON_ENROLLING_SITES,
  REQUIRED_ADMINISTRATIVE_IMV_NON_ENROLLING_SITES,
  SUBJECT_REPORTING_AVERAGE_FREQUENCY,
  SUBJECT_REPORTING_HOURS_TO_GENERATE,
  STUDY_REPORTING_HOURS_TO_GENERATE,
} from "../edit.schedule.event.modal/form.constants";
import { formatDate } from "../../../../app/helpers/utils";
import moment from "moment";

export const ViewScheduleEventForm = ({
  SOEEventParameters,
  isProtocolVersionNA,
  isProtocolDateNA,
}) => {
  const classes = useStyles();
  const { study } = useStudyPlan();

  // Mock errors object for view-only mode (no actual validation errors)
  const errors = {};

  // Mock functions for view-only mode (no actual changes)
  const handleChange = () => {};
  const handleProtocolVersionNAToggle = () => {};
  const handleProtocolDateNAToggle = () => {};
  const handleCurrencyChange = () => {};

  // eslint-disable-next-line no-unused-vars
  const getStudyPlanName = (studyPlanId) => {
    const studyPlan = study?.studyPlans?.find(sp => sp.studyPlanId === studyPlanId);
    return studyPlan ? studyPlan.studyPlanName : "N/A";
  };

  // eslint-disable-next-line no-unused-vars
  const formatDateValue = (dateValue) => {
    if (!dateValue) return "N/A";
    return moment(dateValue).format("DD/MMM/YYYY");
  };

  // eslint-disable-next-line no-unused-vars
  const DisplayField = ({ label, value, isRequired = false }) => (
    <FieldContainer>
      <FieldLabel>
        {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
      </FieldLabel>
      <FieldValue>{value || "N/A"}</FieldValue>
    </FieldContainer>
  );

  return (
    <>
      <VContainer>
        <h5>General</h5>
        <FormControl variant="outlined">
          <TextField
            style={{ width: "100%" }}
            value={SOEEventParameters.scheduleOfEventsName}
            variant="outlined"
            label="SOE Name"
            InputLabelProps={{ classes }}
            required
            disabled={true}
            error={!!errors.scheduleOfEventsName}
            inputProps={{
              maxLength: 50,
              readOnly: true,
            }}
            helperText={
              <AlignBetween>
                <span>{errors.scheduleOfEventsName}</span>
                <span>{`${
                  (SOEEventParameters.scheduleOfEventsName || "").length
                } / ${50}`}</span>
              </AlignBetween>
            }
            size="small"
          />
        </FormControl>
        <FormControl variant="outlined">
          <TextField
            style={{ width: "100%" }}
            value={SOEEventParameters.scheduleOfEventsDescr}
            error={errors.scheduleOfEventsDescr}
            variant="outlined"
            label="SOE Description"
            InputLabelProps={{ classes }}
            required
            disabled={true}
            multiline
            inputProps={{
              maxLength: 2000,
              readOnly: true,
            }}
            rows={4}
            helperText={
              <AlignBetween>
                <span>{errors.scheduleOfEventsDescr}</span>
                <span>{`${
                  (SOEEventParameters.scheduleOfEventsDescr || "").length
                } / ${2000}`}</span>
              </AlignBetween>
            }
            size="small"
          />
        </FormControl>
        <ProtocolVersionContainer>
          <ProtocolInputContainer>
            <FieldWrapper isDisabled={isProtocolVersionNA}>
              <TextField
                style={{ width: "100%" }}
                value={
                  isProtocolVersionNA
                    ? ""
                    : SOEEventParameters.protocolVersion || ""
                }
                variant="outlined"
                label="Protocol Version"
                InputLabelProps={{ classes }}
                required={!isProtocolVersionNA}
                disabled={true}
                error={!isProtocolVersionNA && !!errors.protocolVersion}
                inputProps={{
                  maxLength: 25,
                  readOnly: true,
                }}
                helperText={
                  <AlignBetween>
                    <span>
                      {!isProtocolVersionNA && errors.protocolVersion}
                    </span>
                    <span>{`${
                      (SOEEventParameters.protocolVersion || "").length
                    } / ${25}`}</span>
                  </AlignBetween>
                }
              />
            </FieldWrapper>
            <NAToggle>
              <span>Not Available</span>
              <CustomSwitch
                checked={isProtocolVersionNA}
                onChange={handleProtocolVersionNAToggle}
                disabled={true}
              />
            </NAToggle>
          </ProtocolInputContainer>
        </ProtocolVersionContainer>
        <ProtocolDateContainer>
          <DatePickerWrapper isDisabled={isProtocolDateNA}>
            <CustomDatePicker
              label="Protocol Date"
              onChange={(value) =>
                !isProtocolDateNA &&
                handleChange("protocolDate", formatDate(value))
              }
              showYearDropdown
              showMonthDropdown
              useShortMonthInDropdown
              required={!isProtocolDateNA}
              disabled={true}
              readOnly={true}
              style={{ zIndex: 100 }}
              error={!isProtocolDateNA && errors.protocolDate}
              dateFormat={"dd/MMM/yyyy"}
              selected={
                isProtocolDateNA ? null : SOEEventParameters.protocolDate
              }
              value={isProtocolDateNA ? null : SOEEventParameters.protocolDate}
            />
          </DatePickerWrapper>
          <NAToggle>
            <span>Not Available</span>
            <CustomSwitch
              checked={isProtocolDateNA}
              onChange={handleProtocolDateNAToggle}
              disabled={true}
            />
          </NAToggle>
        </ProtocolDateContainer>

        <CurrencyDropdown
          value={SOEEventParameters.currency}
          onChange={handleCurrencyChange}
          label="Currency"
          required={false}
          size="small"
          variant="outlined"
          style={{ width: "100%" }}
          error={!!errors.currency}
          helperText={errors.currency}
          disabled={true}
          readOnly={true}
        />

        <FormControl variant="outlined">
          <TextField
            label="SOE Status"
            id="soe-status"
            variant="outlined"
            fullWidth
            select
            disabled={true}
            value={SOEEventParameters.scheduleOfEventsStatus}
            size="small"
            InputProps={{ readOnly: true }}
          >
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Final">Final</MenuItem>
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            error={errors.visitIntervalType}
            required
            id="visit-interval-type-id"
            value={SOEEventParameters.visitIntervalType}
            label="Visit Interval Type"
            InputProps={{ readOnly: true }}
          >
            {VISIT_INTERVAL_TYPE_OPTIONS?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            error={errors.visitWindowIntervalType}
            required
            id="visit-window-interval-type-id"
            value={SOEEventParameters.visitWindowIntervalType}
            label="Visit Window Interval Type"
            InputProps={{ readOnly: true }}
          >
            {VISIT_INTERVAL_TYPE_OPTIONS?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl variant="outlined">
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="study-plan"
            value={SOEEventParameters.studyPlanId}
            label="Study Plan"
            InputProps={{ readOnly: true }}
          >
            {study?.studyPlans?.map((sp) => (
              <MenuItem key={sp.studyPlanId} value={sp.studyPlanId}>{sp.studyPlanName}</MenuItem>
            ))}
          </TextField>
        </FormControl>
      </VContainer>
      <VContainer>
        <h5>Clinical Strategy</h5>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            error={errors.CDAReviewPctTime}
            required
            id="cdareviewprctime"
            value={SOEEventParameters.CDAReviewPctTime}
            label="CDA Review % Time"
            InputProps={{ readOnly: true }}
          >
            {CDR_REVIEW_OPTIONS?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl variant="outlined">
          <TextField
            style={{ width: "100%" }}
            value={SOEEventParameters.assessorName}
            variant="outlined"
            label="Risk Strategy Assessor Name"
            InputLabelProps={{ classes }}
            disabled={true}
            multiline
            inputProps={{
              maxLength: 100,
              readOnly: true,
            }}
            helperText={
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <span>{`${
                  (SOEEventParameters.assessorName || "").length
                } / ${100}`}</span>
              </div>
            }
            size="small"
          />
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="additional-time-on-site-factor-id"
            value={SOEEventParameters.AddlTimeOnSitePct}
            label="Additional Time on Site factor"
            InputProps={{ readOnly: true }}
          >
            {ADDITIONAL_TIME_ON_SITE_FACTOR?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="estimated-activated-non-enrolling-id"
            value={SOEEventParameters.EstActivatedNonEnrollingSitesPct}
            label="Estimated Activated Non-enrolling Sites"
            InputProps={{ readOnly: true }}
          >
            {ESTIMATED_ACTIVATED_NON_ENROLLING_SITES?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="required-administrative-imv-id"
            value={SOEEventParameters.ReqAdminIMVNonEnrollingSites}
            label='Required "Administrative" IMV for Non-Enrolling Sites '
            InputProps={{ readOnly: true }}
          >
            {REQUIRED_ADMINISTRATIVE_IMV_NON_ENROLLING_SITES?.map((type) => (
              <MenuItem key={type} value={type === "<blank value>" ? "" : type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="participant-reporting-average-frequency-id"
            value={SOEEventParameters.SubjectReportingAvgFrequency}
            label="Participant Reporting, Average Frequency "
            InputProps={{ readOnly: true }}
          >
            {SUBJECT_REPORTING_AVERAGE_FREQUENCY?.map((type) => (
              <MenuItem key={type} value={type === "<blank value>" ? "" : type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="participant-reporting-hours-to-generate-id"
            value={SOEEventParameters.SubjectReportingHoursToGenerate}
            label="Participant Reporting, Hours to Generate "
            InputProps={{ readOnly: true }}
          >
            {SUBJECT_REPORTING_HOURS_TO_GENERATE?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="study-reporting-average-frequency-id"
            value={SOEEventParameters.StudyReportingAvgFrequency}
            label="Study Reporting, Average Frequency "
            InputProps={{ readOnly: true }}
          >
            {SUBJECT_REPORTING_AVERAGE_FREQUENCY?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl
          style={{ width: "100%" }}
          variant="outlined"
        >
          <TextField
            variant="outlined"
            fullWidth
            select
            size="small"
            disabled={true}
            id="study-reporting-hours-to-generate-id"
            value={SOEEventParameters.StudyReportingHoursToGenerate}
            label="Study Reporting, Hours to Generate "
            InputProps={{ readOnly: true }}
          >
            {STUDY_REPORTING_HOURS_TO_GENERATE?.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </FormControl>
        <FormControl variant="outlined">
          <TextField
            style={{ width: "100%" }}
            value={SOEEventParameters.StudyAdhocReview}
            variant="outlined"
            label="Ad Hoc Review"
            InputLabelProps={{ classes }}
            disabled={true}
            type="number"
            size="small"
            inputProps={{ readOnly: true }}
          />
        </FormControl>
      </VContainer>
    </>
  );
};


const useStyles = makeStyles({
  asterisk: {
    color: "red",
  },
});

const AlignBetween = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const VContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2em;
  flex: 1;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: #333;
  padding: 8px 12px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  min-height: 20px;
  
  &:empty::after {
    content: "N/A";
    color: #999;
    font-style: italic;
  }
`;

const RequiredIndicator = styled.span`
  color: #f44336;
  margin-left: 2px;
`;

const ProtocolVersionContainer = styled.div`
  width: 100%;
`;

const ProtocolInputContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1em;
`;

const ProtocolDateContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1em;
`;

const FieldWrapper = styled.div`
  flex: 1;

  && .MuiInputLabel-root.Mui-disabled {
    color: var(--text-secondary);
  }

  && .MuiInputLabel-root.Mui-disabled.MuiInputLabel-shrink {
    color: var(--text-secondary);
  }

  && .MuiOutlinedInput-root.Mui-disabled {
    background-color: var(--blue-gray-100);
  }

  && .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  && .MuiOutlinedInput-input.Mui-disabled {
    color: rgba(0, 0, 0, 0.38);
    -webkit-text-fill-color: rgba(0, 0, 0, 0.38);
  }

  && .MuiOutlinedInput-input::placeholder {
    color: var(--text-secondary);
    opacity: 1;
  }
`;
const DatePickerWrapper = styled.div`
  flex: 1;
  pointer-events: ${(props) => (props.isDisabled ? "none" : "auto")};

  && .MuiInputLabel-root.Mui-disabled {
    color: var(--text-secondary);
  }

  && .MuiInputLabel-root.Mui-disabled.MuiInputLabel-shrink {
    color: var(--text-secondary);
  }

  && .MuiOutlinedInput-root.Mui-disabled {
    background-color: var(--blue-gray-100);
  }

  && .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  && .MuiOutlinedInput-input.Mui-disabled {
    color: rgba(0, 0, 0, 0.38);
    -webkit-text-fill-color: rgba(0, 0, 0, 0.38);
  }

  && .MuiOutlinedInput-input::placeholder {
    color: var(--text-secondary);
    opacity: 1;
  }
`;


const NAToggle = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;

  span {
    order: -1;
  }
`;